import './residence.scss'
import 'swiper/scss'
import { Redirect, useParams } from 'react-router'
import { IonBackdrop, IonCol, IonGrid, IonLoading, IonRow } from '@ionic/react'
import {
  BookingBox,
  ThreeBarsBox,
  ResidenceDetailMain,
  ResidenceDetailHeader,
  RoomSelectionList,
  ResidenceDetailSecondary,
  BackLink,
} from 'components'
import { useResidence, useSearchQueryString, useHeightListener, useWindowDimensions, usePageTitle } from 'hooks'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { IBookingStorage, IRoom, ISelectedRoom, IPaidService } from 'interfaces'
import { addDays, differenceInDays, format, isBefore, startOfToday, subDays } from 'date-fns'
import { routeService, numberService, dateService, storageService, stringService, loggerService } from 'services'
import { v4 as uuidv4 } from 'uuid'
import { authContext } from 'contexts/'
import BasePage from 'pages/base-page/base-page.page'
import { useTranslation } from 'react-i18next'

const Residence: React.FC = () => {
  const errorHandler = useMemo(
    () => (err: any) => {
      loggerService.log('ERROR', err) //TODO
    },
    [],
  )
  const auth = useContext(authContext)
  const userData = auth?.getUserData()
  const { searchParams, queryString } = useSearchQueryString(userData?.tipoProfilo.id)
  const [searchMode, setSearchMode] = useState<boolean>(!!searchParams?.dateFrom && !!searchParams.dateTo)
  const { t } = useTranslation()
  const [roomsSelection, setRoomsSelection] = useState(false)
  const [roomsSelected, setRoomsSelected] = useState<ISelectedRoom[]>([])
  const [selectedServices, setSelectedServices] = useState<IPaidService[]>([])
  const [booking, setBooking] = useState<IBookingStorage>()
  const { residenceId } = useParams<any>()
  const residenceSearchParams = useMemo(() => {
    return { ...searchParams, residenceId }
  }, [residenceId, searchParams])
  const { residence, loading } = useResidence(residenceId, residenceSearchParams, errorHandler)
  const [threeBarsBoxRef, threeBarsBoxHeight] = useHeightListener()
  const windowDimension = useWindowDimensions()
  const [overflowActive, setOverflowActive] = useState(false)

  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined)
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined)
  const [bookingBackdropVisible, setBookingBackdropVisible] = useState(false)

  useEffect(() => {
    setOverflowActive((threeBarsBoxHeight || 0) + 1 > windowDimension.height)
  }, [threeBarsBoxHeight, windowDimension.height])

  useEffect(() => {
    let { from: dateFrom_, to: dateTo_ } = dateService.getDefaultDatesRange()

    if (searchParams) {
      if (searchParams.dateFrom && searchParams.dateTo) {
        dateFrom_ = searchParams.dateFrom
        dateTo_ = searchParams.dateTo
      } else if (searchParams.dateFrom && !searchParams.dateTo) {
        dateFrom_ = searchParams.dateFrom
        dateTo_ = addDays(dateFrom_, 7)
      } else if (searchParams.dateTo && !searchParams.dateFrom) {
        dateTo_ = searchParams.dateTo
        dateFrom_ = subDays(dateTo_, 7)
        const today = startOfToday()
        if (isBefore(dateFrom_, today)) dateFrom_ = today
      }
    }

    setDateFrom(dateFrom_)
    setDateTo(dateTo_)
  }, [searchParams])

  useEffect(() => {
    setSearchMode(!!searchParams?.dateFrom && !!searchParams?.dateTo)
  }, [searchParams])

  useEffect(() => {
    if (residence?.camere) {
      setRoomsSelected(
        residence.camere.map((r: IRoom) => {
          return { room: r, value: 0 }
        }),
      )
    }
  }, [residence?.camere])

  const changeRoomSelection = (room: IRoom, value: number) => {
    setRoomsSelected((roomsSelected_) =>
      roomsSelected_.map((rs) => {
        if (rs.room.id === room.id) return { room: room, value: value }
        return rs
      }),
    )
  }

  const saveBooking = () => {
    if (can_book) {
      const uuid = uuidv4()
      const rooms_ = roomsSelected.filter((r) => r.value > 0)

      const new_booking: IBookingStorage = {
        residence: residence!,
        dateFrom: format(dateFrom, dateService.LOCAL_STORAGE_DATE_FORMAT),
        dateTo: format(dateTo, dateService.LOCAL_STORAGE_DATE_FORMAT),
        guestsPerRoom: rooms_,
        extraServices: selectedServices,
        uuid: uuid,
        booking_date: new Date(),
        eventCode: searchParams?.eventCode,
        totalPrice: numberService.getTotalPrice(rooms_, selectedServices),
        queryParams: queryString,
      }

      storageService.saveBooking(new_booking)
      setBooking(new_booking)
    }
  }

  const backClick = useCallback(() => {
    if (residence?.camere) {
      setRoomsSelected(
        residence.camere.map((r: IRoom) => {
          return { room: r, value: 0 }
        }),
      )
      setRoomsSelection(false)
    }
  }, [residence])

  let titlePage = !!residence ? stringService.capitalizeFirstLetter(residence!.nome) : ''
  usePageTitle(titlePage!)

  if (booking) return <Redirect to={routeService.generateRoutePath('/booking-confirm/:uuidBooking', { uuidBooking: booking.uuid })} />

  const numDays = dateFrom && dateTo ? differenceInDays(dateTo, dateFrom) : -1
  const realRoomsSelected = roomsSelected.filter((rs) => rs.value > 0)
  const can_book = !booking && !!dateFrom && !!dateTo && (!roomsSelection || realRoomsSelected.length > 0)
  return (
    <BasePage pageClass="edisu-page-residence" paddingHeight={threeBarsBoxHeight}>
      <IonBackdrop className={'backdrop ' + (bookingBackdropVisible ? '' : 'ion-hide')} />
      {!!searchParams?.dateFrom ? null : <BackLink link={'/residences'} style={{ marginBottom: '30px' }}></BackLink>}

      {loading && !residence ? (
        <IonLoading isOpen={true} message={t('loading')} duration={5000} />
      ) : !!residence ? (
        <div className="detail-container">
          <ResidenceDetailHeader residence={residence} />

          <IonGrid className="detail-grid">
            <IonRow className="top-row">
              <IonCol size="12" sizeLg="7">
                {!roomsSelection ? (
                  <ResidenceDetailMain residence={residence} />
                ) : (
                  residence.camere && (
                    <>
                      {<BackLink onClick={backClick} style={{ marginBottom: '30px' }}></BackLink>}

                      <RoomSelectionList
                        rooms={residence.camere}
                        numDays={numDays}
                        dateFrom={dateFrom ? format(dateFrom, 'd MMM') : ''}
                        dateTo={dateTo ? format(dateTo, 'd MMM') : ''}
                        onChangeRoomSelection={changeRoomSelection}
                      />
                    </>
                  )
                )}
              </IonCol>
              <IonCol size="12" sizeLg="5">
                {searchMode && (
                  <ThreeBarsBox nodeRef={threeBarsBoxRef} extraClassName={overflowActive ? 'overflow-bar' : ''}>
                    <BookingBox
                      enableBookNowButton={can_book}
                      services={residence.serviziPagamento || []}
                      selectedServices={selectedServices}
                      setSelectedServices={setSelectedServices}
                      bookFunction={roomsSelection ? () => saveBooking() : () => setRoomsSelection(true)}
                      backdropFunction={setBookingBackdropVisible}
                      priceMin={residence.camere && Math.min(...residence.camere?.map((c: IRoom) => c.prezzo))}
                      dateFrom={dateFrom}
                      dateTo={dateTo}
                      rating={'' + residence.gradimento}
                      selectedRooms={realRoomsSelected}
                      numDays={numDays}
                      enableChangeDates={false}
                      noAvailability={!!!residence.camere || residence.camere.length === 0}
                      eventCode={searchParams?.eventCode}
                      profile={searchParams?.profileTypeId}
                      guestsSelection={searchParams?.guestsPerRoom}
                    />
                  </ThreeBarsBox>
                )}
              </IonCol>

              {!roomsSelection && (
                <IonCol size="12">
                  <ResidenceDetailSecondary residence={residence} />
                </IonCol>
              )}
            </IonRow>
          </IonGrid>
        </div>
      ) : (
        <p>{t('residence_not_exist')}</p>
      )}
    </BasePage>
  )
}

export default Residence
